import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { Button, Input } from './ui/components';
import { Icon } from './ui/icons';
import { AuthShowcase, Eyebrow } from './pages/auth-redesign/showcase';
import { registerPasskey, isPasskeySupported } from './auth/passkey';

const API = process.env.REACT_APP_API_URL;
const POST_SIGNIN = '/lock';

type Security = {
  email?: string;
  hasAuthenticator?: boolean;
  hasPasskey?: boolean;
  backupCodesRemaining?: number;
  hasTerminalPin?: boolean;
  setupRequired?: string;
};

const get = (p: string) =>
  fetch(`${API}${p}`, { credentials: 'include' }).then((r) => (r.ok ? r.json() : Promise.reject(r)));
const post = (p: string, body?: any) =>
  fetch(`${API}${p}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: body ? JSON.stringify(body) : undefined,
  });

// First-login security setup. The API gates the session into these routes (setupRequired != 'none')
// until every required factor is enrolled. We re-read /security after each step and follow it.
const SetupGate = () => {
  const navigate = useNavigate();
  const { completeMfa } = useAuth() as any;

  const [sec, setSec] = useState<Security | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // authenticator sub-state
  const [qr, setQr] = useState<{ image: string; secret?: string } | null>(null);
  const [totp, setTotp] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);
  // pin sub-state
  const [pin, setPin] = useState('');

  const refresh = useCallback(async () => {
    const s: Security = await get('/Authentication/security');
    setSec(s);
    return s;
  }, []);

  useEffect(() => {
    refresh().catch(() => setError('Could not load your security status.'));
  }, [refresh]);

  const step = (sec?.setupRequired || '').toLowerCase();

  // If setup is done, finish the walk and go into the app.
  useEffect(() => {
    if (sec && step === 'none' && !backupCodes) {
      (async () => {
        await post('/Authentication/setup/complete');
        if (completeMfa) { try { await completeMfa(); } catch (e) { /* noop */ } }
        navigate(POST_SIGNIN, { replace: true });
      })();
    }
  }, [sec, step, backupCodes, completeMfa, navigate]);

  const loadQr = useCallback(async () => {
    setError('');
    try {
      const data = await get('/Authentication/authenticator/qr');
      setQr({ image: data.image, secret: data.secret });
    } catch (e) {
      setError('Could not start authenticator setup.');
    }
  }, []);

  useEffect(() => {
    if (step.includes('auth') && !qr && !backupCodes) loadQr();
  }, [step, qr, backupCodes, loadQr]);

  const verifyTotp = async () => {
    setBusy(true);
    setError('');
    try {
      const res = await post('/Authentication/authenticator/verify', { Totp: totp.trim() });
      if (!res.ok) {
        setError('That code was not correct. Check your authenticator app and try again.');
        return;
      }
      const data = await res.json().catch(() => ({}));
      if (Array.isArray(data.backupCodes) && data.backupCodes.length) {
        setBackupCodes(data.backupCodes); // show once, then continue
      } else {
        setTotp('');
        setQr(null);
        await refresh();
      }
    } catch (e) {
      setError('Verification failed. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const continueAfterCodes = async () => {
    setBackupCodes(null);
    setTotp('');
    setQr(null);
    await refresh();
  };

  const enrollPasskey = async () => {
    setBusy(true);
    setError('');
    try {
      await registerPasskey({ email: sec?.email || undefined });
      await refresh();
    } catch (e: any) {
      if (e?.name === 'NotAllowedError') setError('Passkey enrollment was cancelled.');
      else setError(e?.message || 'Could not enroll a passkey.');
    } finally {
      setBusy(false);
    }
  };

  const skipPasskey = async () => {
    // Passkey enrolment is offered, not forced; tell the server we are done with this step.
    setBusy(true);
    try { await post('/Authentication/setup/complete'); await refresh(); }
    finally { setBusy(false); }
  };

  const setTerminalPin = async () => {
    setBusy(true);
    setError('');
    try {
      const res = await post('/Authentication/pin/set', { pin: pin.trim() });
      if (!res.ok) {
        const t = await res.text().catch(() => '');
        setError(t || 'Could not set your PIN. Choose a less common 4–6 digit PIN.');
        return;
      }
      setPin('');
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  const Head = ({ title, sub }: { title: string; sub: string }) => (
    <header className="authx-form__head">
      <Eyebrow>Secure your account</Eyebrow>
      <h1 className="authx-form__title">{title}</h1>
      <p className="authx-form__sub">{sub}</p>
    </header>
  );

  const Err = () =>
    error ? (
      <div className="authx-alert authx-alert--error" role="alert">
        <Icon.CircleAlert size={16} />
        <span>{error}</span>
      </div>
    ) : null;

  // ---- render the current step -------------------------------------------------
  let body: React.ReactNode;

  if (!sec) {
    body = <Head title="Loading…" sub="Checking what your account still needs." />;
  } else if (backupCodes) {
    body = (
      <>
        <Head title="Save your backup codes" sub="Keep these somewhere safe. Each works once if you lose your device." />
        <Err />
        <div className="authx-form__fields">
          <div className="authx-alert" role="status" style={{ display: 'block', fontFamily: 'monospace', lineHeight: 1.9 }}>
            {backupCodes.map((c) => (
              <div key={c}>{c}</div>
            ))}
          </div>
        </div>
        <div className="authx-form__actions">
          <Button type="button" variant="primary" fullWidth size="lg" loading={busy} onClick={continueAfterCodes}>
            I've saved them — continue
          </Button>
        </div>
      </>
    );
  } else if (step.includes('auth')) {
    body = (
      <>
        <Head title="Set up your authenticator" sub="Scan the QR with Google Authenticator / 1Password, then enter the 6-digit code." />
        <Err />
        <div className="authx-form__fields">
          {qr ? (
            <img
              src={`data:image/png;base64,${qr.image}`}
              alt="Authenticator QR code"
              style={{ width: 180, height: 180, alignSelf: 'center', borderRadius: 8 }}
            />
          ) : (
            <p className="authx-form__sub">Generating your QR code…</p>
          )}
          {qr?.secret && (
            <p className="authx-form__sub" style={{ textAlign: 'center' }}>
              Can't scan? Enter this key: <code>{qr.secret}</code>
            </p>
          )}
          <Input
            label="6-digit code"
            inputMode="numeric"
            value={totp}
            placeholder="123456"
            leadingIcon={<Icon.Lock size={16} />}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTotp(e.target.value)}
          />
        </div>
        <div className="authx-form__actions">
          <Button type="button" variant="primary" fullWidth size="lg" loading={busy} disabled={busy || totp.trim().length < 6} onClick={verifyTotp}>
            Verify & continue
          </Button>
        </div>
      </>
    );
  } else if (step.includes('passkey')) {
    body = (
      <>
        <Head title="Add a passkey" sub="Sign in next time with Face ID, Touch ID, or Windows Hello — no code needed." />
        <Err />
        <div className="authx-form__actions">
          {isPasskeySupported() && (
            <Button type="button" variant="primary" fullWidth size="lg" loading={busy} leadingIcon={<Icon.Fingerprint size={16} />} onClick={enrollPasskey}>
              Add a passkey
            </Button>
          )}
          <Button type="button" variant="secondary" fullWidth size="lg" disabled={busy} onClick={skipPasskey}>
            Skip for now
          </Button>
        </div>
      </>
    );
  } else if (step.includes('pin')) {
    body = (
      <>
        <Head title="Set your terminal PIN" sub="This is what you'll tap in to unlock the shared terminal during service." />
        <Err />
        <div className="authx-form__fields">
          <Input
            label="Choose a 4–6 digit PIN"
            inputMode="numeric"
            type="password"
            value={pin}
            placeholder="••••"
            leadingIcon={<Icon.Lock size={16} />}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPin(e.target.value)}
          />
        </div>
        <div className="authx-form__actions">
          <Button type="button" variant="primary" fullWidth size="lg" loading={busy} disabled={busy || pin.trim().length < 4} onClick={setTerminalPin}>
            Set PIN & continue
          </Button>
        </div>
      </>
    );
  } else {
    body = <Head title="Finishing up…" sub="Completing your sign-in." />;
  }

  return (
    <AuthShowcase>
      <div className="authx-form">{body}</div>
    </AuthShowcase>
  );
};

export default SetupGate;
